import React, { Fragment } from 'react'
import { Menu, MenuItem } from '../ui/menu'
import { EllipsisMenu } from '../../icons/icons'
import { AddHeaderButton } from './RuleEditorPanels'
import PromotionRuleI = Models.PromotionRuleI

// ── Helpers ───────────────────────────────────────────────────────────────────

const OP: Record<string, string> = {
    '>': '>',
    '>=': '≥',
    '<': '<',
    '<=': '≤',
    '=': '=',
}

const fmt = (op: string) => OP[op] ?? op

const ok = (v: string | number | null | undefined): boolean =>
    v !== null && v !== undefined && v !== ''

interface ConditionSpec {
    label: string
    valueKey: keyof PromotionRuleI
    opKey: keyof PromotionRuleI
    prefix?: string
    suffix?: string
}

const CONDITIONS: ConditionSpec[] = [
    { label: 'Orders', valueKey: 'orders_value', opKey: 'orders_operator' },
    { label: 'Adj. Orders', valueKey: 'adjusted_orders_value', opKey: 'adjusted_orders_operator' },
    {
        label: 'Spend',
        valueKey: 'spend_value',
        opKey: 'spend_operator',
        prefix: window.marketplace?.currency_symbol ?? '$',
    },
    { label: 'ACoS', valueKey: 'acos_value', opKey: 'acos_operator', suffix: '%' },
    {
        label: 'Blended ACoS',
        valueKey: 'blended_acos_value',
        opKey: 'blended_acos_operator',
        suffix: '%',
    },
    { label: 'Clicks', valueKey: 'clicks_value', opKey: 'clicks_operator' },
    {
        label: 'Pages Read',
        valueKey: 'adjusted_pages_read_value',
        opKey: 'adjusted_pages_read_operator',
    },
    {
        label: 'Est. Royalties',
        valueKey: 'adjusted_estimated_royalties_value',
        opKey: 'adjusted_estimated_royalties_operator',
        prefix: window.marketplace?.currency_symbol ?? '$',
    },
    { label: 'Blended Profit', valueKey: 'blended_profit_value', opKey: 'blended_profit_operator' },
]

function buildConditionParts(rule: PromotionRuleI): string[] {
    const parts: string[] = []
    for (const c of CONDITIONS) {
        const val = rule[c.valueKey]
        const op = rule[c.opKey] as string
        if (ok(val)) {
            parts.push(`${c.label} ${fmt(op)} ${c.prefix ?? ''}${val}${c.suffix ?? ''}`)
        }
    }
    parts.push(
        ok(rule.lookback_days) ? `over the last ${rule.lookback_days} days` : 'over its lifetime',
    )
    return parts
}

function buildBidText(rule: PromotionRuleI): string {
    const sym = window.marketplace?.currency_symbol ?? '$'
    if (ok(rule.bid_set)) return `setting the bid to ${sym}${rule.bid_set}`
    if (ok(rule.bid_increase_amount)) {
        const amt = Number(rule.bid_increase_amount)
        return amt >= 0
            ? `raising the bid by ${sym}${amt}`
            : `lowering the bid by ${sym}${Math.abs(amt)}`
    }
    if (ok(rule.bid_increase_percent)) {
        const pct = Number(rule.bid_increase_percent)
        return pct >= 0 ? `raising the bid by ${pct}%` : `lowering the bid by ${Math.abs(pct)}%`
    }
    return ''
}

function countPromoteItems(rule: PromotionRuleI): number {
    return (rule.rule_items ?? []).filter(
        (i) => i.promotion_broad || i.promotion_phrase || i.promotion_exact || i.promotion_products,
    ).length
}

function countNegateItems(rule: PromotionRuleI): number {
    return (rule.rule_items ?? []).filter(
        (i) => i.negation_phrase || i.negation_exact || i.negation_products,
    ).length
}

// ── Sub-components ────────────────────────────────────────────────────────────

const StatusBadge = ({ enabled }: { enabled: boolean }) =>
    enabled ? (
        <span className="inline-flex items-center gap-1 text-[10px] font-[500] text-emerald-600/80 px-1 py-0.5 flex-shrink-0">
            <span className="size-1 rounded-full bg-emerald-400 flex-shrink-0" />
            Active
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 text-[10px] font-[500] text-[#B0BEC9] px-1 py-0.5 flex-shrink-0">
            <span className="size-1 rounded-full bg-[#CBD5E1] flex-shrink-0" />
            Paused
        </span>
    )

interface RuleCardProps {
    rule: PromotionRuleI
    screenshotMode: boolean
    onEdit: (rule: PromotionRuleI) => void
    onToggle: (rule: PromotionRuleI, enabled: boolean) => void
    onRemove: (rule: PromotionRuleI) => void
}

const RuleCard = ({ rule, screenshotMode, onEdit, onToggle, onRemove }: RuleCardProps) => {
    const conditionParts = buildConditionParts(rule)
    const bidText = buildBidText(rule)
    const promoteCount = countPromoteItems(rule)
    const negateCount = countNegateItems(rule)
    const hasBid = ok(rule.bid_set) || ok(rule.bid_increase_amount) || ok(rule.bid_increase_percent)
    const isPositiveBid =
        Number(rule.bid_increase_amount) > 0 || Number(rule.bid_increase_percent) > 0

    return (
        <div
            className="group flex items-start gap-4 px-4 py-3.5 hover:bg-[#FAFBFC] active:bg-[#EFF3F8] transition-colors duration-100 cursor-pointer relative hover:z-[1]"
            onClick={() => onEdit(rule)}
        >
            {/* Col 1 — Name + badge inline, conditions below */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                    <span
                        className={
                            'text-[14px] font-[650] tracking-[-0.01em] text-[#0E2947] group-hover:text-[#1A6FD4] transition-colors truncate leading-snug' +
                            (screenshotMode ? ' blur' : '')
                        }
                    >
                        {rule.name || 'Untitled rule'}
                    </span>
                    <StatusBadge enabled={rule.enabled} />
                </div>
                <div className="mt-1 space-y-0.5">
                    {conditionParts.slice(0, -1).map((part, i) => (
                        <div key={i} className="text-[11.5px] text-[#7289A3] leading-snug">
                            {part}
                        </div>
                    ))}
                    <div className="text-[11px] text-[#B0BEC9] italic leading-snug">
                        {conditionParts[conditionParts.length - 1]}
                    </div>
                </div>
            </div>

            {/* Col 2 — Action summary */}
            <div className="w-64 flex-shrink-0 space-y-0.5 pt-0.5">
                {promoteCount > 0 && (
                    <div className="space-y-0.5">
                        <div className="text-[12px] text-[#0E2947]">
                            Promote to <span className="font-[600]">{promoteCount}</span> ad group
                            {promoteCount !== 1 ? 's' : ''}
                        </div>
                        {hasBid && bidText ? (
                            <div
                                className={`text-[12px] pl-3 ${isPositiveBid ? 'text-emerald-700' : 'text-[#7289A3]'}`}
                            >
                                {bidText}
                            </div>
                        ) : (
                            <div className="text-[12px] pl-3 text-[#7289A3]">
                                keeping the current bid
                            </div>
                        )}
                    </div>
                )}
                {negateCount > 0 && (
                    <div className="text-[12px] text-[#0E2947]">
                        Negate in <span className="font-[600]">{negateCount}</span> ad group
                        {negateCount !== 1 ? 's' : ''}
                    </div>
                )}
                {promoteCount === 0 && negateCount === 0 && (
                    <div className="text-[12px] text-[#94A3B8]">No targeting configured</div>
                )}
            </div>

            {/* Ellipsis menu — isolated from card click */}
            <div
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
            >
                <EllipsisMenu
                    content={
                        <Menu>
                            <MenuItem
                                text={rule.enabled ? 'Pause rule' : 'Enable rule'}
                                onClick={() => onToggle(rule, !rule.enabled)}
                            />
                            <MenuItem text="Remove rule" onClick={() => onRemove(rule)} />
                        </Menu>
                    }
                />
            </div>
        </div>
    )
}

// ── Main component ────────────────────────────────────────────────────────────

interface PromotionRulesNewProps {
    rules: PromotionRuleI[]
    screenshotMode: boolean
    setRules: (fn: (prev: PromotionRuleI[]) => PromotionRuleI[]) => void
    addRule: () => void
    removeRule: (rule: PromotionRuleI) => void
    handleAddRule: (row: PromotionRuleI) => void
}

export const PromotionRulesNew = ({
    rules,
    screenshotMode,
    setRules,
    addRule,
    removeRule,
    handleAddRule,
}: PromotionRulesNewProps) => {
    const handleToggle = (rule: PromotionRuleI, enabled: boolean) => {
        setRules((prev) =>
            prev.map((r) =>
                (rule.id && rule.id === r.id) || (rule.uuid && rule.uuid === r.uuid)
                    ? { ...r, enabled }
                    : r,
            ),
        )
    }

    return (
        <Fragment>
            {/* Section header */}
            <div className="pt-2.5 pb-2 flex items-center justify-between">
                <div className="text-[15px] font-[600] text-[#0E2947]">
                    Rules
                    <span className="ml-2 text-[12px] font-[400] text-[#94A3B8]">
                        {rules.length}
                    </span>
                </div>
                <AddHeaderButton label="Add rule" onClick={addRule} />
            </div>

            {/* Rule cards */}
            {rules.map((rule) => (
                <RuleCard
                    key={rule.id ?? rule.uuid}
                    rule={rule}
                    screenshotMode={screenshotMode}
                    onEdit={handleAddRule}
                    onToggle={handleToggle}
                    onRemove={removeRule}
                />
            ))}
        </Fragment>
    )
}
